import { useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FOUNDING_DISCOUNT_PERCENT, FOUNDING_NOTICE_DAYS } from "@metrickle/schema/plans";
import { forgetInvite, redeemInvite, rememberedInvite, requestGrowth, useMyAccess } from "../lib/early-access";
import { track } from "../lib/track";
import { Button, Callout, Card, CardHeader, Field, FieldError, Input, Textarea } from "./ui";

const fmtDate = (t: number) => new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

/**
 * On a Free workspace during early access: enter an invite code, or ask for Growth (free while we
 * launch, as a founding workspace). A code from an invite link that didn't work at sign-up waits here.
 */
export function GrowthRequest({ orgId, workspaceName }: { orgId: string; workspaceName: string }) {
  const qc = useQueryClient();
  const access = useMyAccess();
  const [code, setCode] = useState(rememberedInvite);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [company, setCompany] = useState<string | null>(null);
  const [website, setWebsite] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const status = useRef<HTMLDivElement>(null);

  const request = access.data?.request ?? null;
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["my-access"] }), qc.invalidateQueries({ queryKey: ["billing"] })]);

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true);
    setCodeError(null);
    try {
      await redeemInvite(code.trim());
      forgetInvite();
      track("early_access_code_redeemed");
      await refresh();
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : String(err));
    } finally {
      setChecking(false);
    }
  };

  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    setRequestError(null);
    try {
      await requestGrowth({ company: company ?? request?.company ?? workspaceName, website: (website ?? request?.website) || null, note: (note ?? request?.note) || null, orgId });
      track("early_access_requested");
      await refresh();
      status.current?.focus();
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  };

  if (!access.data || access.data.open || access.data.growth) return null;
  const pending = request && !request.decision;
  return (
    <Card>
      <CardHeader
        title="Get Growth free while we launch"
        subtitle={`Replay, heatmaps, more apps and every protected task. Founding customers keep ${FOUNDING_DISCOUNT_PERCENT}% off for good when paid plans start, with ${FOUNDING_NOTICE_DAYS} days' notice first.`}
      />
      <div className="grid gap-6 border-t border-line px-4 py-4 @3xl:grid-cols-2">
        <div ref={status} tabIndex={-1} role="status" className="outline-none @3xl:col-span-2 empty:hidden">
          {pending ? (
            <Callout tone="info" title="You've asked for Growth.">
              We read every request and usually reply within a day or two, by email. Your workspace keeps working on Free in the meantime. Asked on {fmtDate(request.requestedAt)}.
            </Callout>
          ) : null}
        </div>

        {!pending && (
          <form onSubmit={submitRequest} className="space-y-3">
            <h3 className="text-sm font-semibold text-ink">Ask for Growth</h3>
            {request?.decision === "declined" && <p className="text-sm text-ink-2">We couldn't open a place last time. Tell us a bit more and we'll look again.</p>}
            <Field label="Company or project">
              <Input required maxLength={120} value={company ?? request?.company ?? workspaceName} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
            </Field>
            <Field label="Website (optional)">
              <Input maxLength={200} value={website ?? request?.website ?? ""} onChange={(e) => setWebsite(e.target.value)} placeholder="example.com" inputMode="url" autoComplete="url" />
            </Field>
            <Field label="What would you like Metrickle to help with? (optional)">
              <Textarea rows={3} maxLength={1000} value={note ?? request?.note ?? ""} onChange={(e) => setNote(e.target.value)} />
            </Field>
            {requestError && <FieldError role="alert">{requestError}</FieldError>}
            <Button variant="primary" pending={sending} pendingLabel="Sending…">
              Ask for Growth
            </Button>
          </form>
        )}

        <form onSubmit={submitCode} className="space-y-3">
          <h3 className="text-sm font-semibold text-ink">Have an invite code?</h3>
          <Field label="Invite code" hint="It looks like MK-7KQ2-HX9D.">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={!!codeError}
              aria-describedby={codeError ? "code-error" : undefined}
            />
          </Field>
          {codeError && (
            <FieldError id="code-error" role="alert">
              {codeError}
            </FieldError>
          )}
          <Button disabled={!code.trim()} pending={checking} pendingLabel="Checking…">
            Use code
          </Button>
        </form>
      </div>
    </Card>
  );
}
